import { useEffect, useState, type ReactElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button, Spinner, ThemeToggle } from '@csa/ui';
import { useAuth } from './AuthProvider.js';
import { autoSignInPending, claimAutoSignIn } from './auto-signin.js';
import { appConfig } from '../lib/app-config.js';

/** How long to wait for the redirect before offering the button instead. */
const REDIRECT_STALLED_MS = 6_000;

/**
 * Sign-in, which normally nobody sees.
 *
 * Opening any URL on the console sends the browser straight to Entra, so
 * this renders as a brief notice on the way out. It becomes a real page only
 * when the automatic redirect has already been tried in this tab and landed
 * back here, or when the navigation did not happen at all -- see
 * `auto-signin.ts` for why that distinction has to be remembered.
 *
 * One button, because the only way in is Entra ID: there are no local
 * accounts to offer and nothing to type.
 */
export function SignInScreen(): ReactElement {
  const { signIn, status, error } = useAuth();
  const config = appConfig();

  // Read before claiming, so the first paint is already the redirect notice
  // rather than the card appearing for a frame and being replaced.
  const [redirecting] = useState(autoSignInPending);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!claimAutoSignIn()) return;
    signIn();
  }, [signIn]);

  /**
   * A redirect that never happens would otherwise leave the user watching a
   * spinner with nothing to do. `loginRedirect` navigates away, so this
   * timer is only ever reached when something stopped it.
   */
  useEffect(() => {
    if (!redirecting) return;
    const timer = setTimeout(() => setStalled(true), REDIRECT_STALLED_MS);
    return () => clearTimeout(timer);
  }, [redirecting]);

  if (redirecting && !stalled && !error) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sunken"
        role="status"
        aria-live="polite"
      >
        <Spinner className="size-6 text-brand" label={null} />
        <p className="text-sm text-muted">Redirecting to Microsoft…</p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-sunken">
      <header className="flex items-center justify-between px-6 py-5">
        <span className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded bg-brand text-sm font-bold text-on-brand">
            C
          </span>
          <span className="text-sm font-semibold tracking-tight text-body">Support Console</span>
        </span>
        <ThemeToggle />
      </header>

      <main className="flex flex-1 items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md">
          <div className="rounded-lg border border-line bg-surface p-8 shadow-card">
            <h1 className="text-xl font-semibold tracking-tight text-body">Sign in</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Use your organisation account. Access is governed by your assigned role, and
              privileged actions require approval from a second person.
            </p>

            {error ? (
              <p
                role="alert"
                className="mt-5 rounded-md border border-line bg-sunken px-3 py-2.5 text-sm text-body"
              >
                {error}
              </p>
            ) : (
              /**
               * Reaching this card at all means the automatic redirect did
               * not complete, so say so. Without it the page looks like the
               * intended first step and the user has no idea anything was
               * attempted, which is the difference between retrying once and
               * raising a ticket.
               */
              <p className="mt-5 rounded-md border border-line bg-sunken px-3 py-2.5 text-sm text-muted">
                Automatic sign-in did not complete. Continue below to try again.
              </p>
            )}

            <Button
              variant="primary"
              size="md"
              className="mt-6 w-full"
              onClick={signIn}
              loading={status === 'initialising'}
              trailingIcon={<ArrowRightIcon className="size-4" aria-hidden="true" />}
            >
              Continue with Microsoft
            </Button>

            <p className="mt-6 flex gap-2.5 text-xs leading-relaxed text-subtle">
              <ShieldCheckIcon className="size-4 shrink-0 text-brand" aria-hidden="true" />
              <span>
                Every query, API call and approval you make is recorded in a tamper-evident audit
                log retained for seven years and reviewed for SOX compliance.
              </span>
            </p>
          </div>

          {config.auth.devBypass ? (
            <p className="mt-4 rounded-md border border-brand/40 bg-brand-subtle px-3 py-2 text-xs text-body">
              Authentication is bypassed in this environment. Never enable this outside local
              development.
            </p>
          ) : null}

          <p className="mt-6 text-center text-xs text-subtle">
            {config.env} · build {config.build.sha.slice(0, 7)}
          </p>
        </div>
      </main>
    </div>
  );
}
